import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { DeletePatientDischarge, GetPatientDischarges } from "../../../../api/ipd/patientDischargeAPI";

const ListPatientDischargeEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPatientDischarges(); // Your API should return a list of OPD Charges entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          billNo: item?.billNo || "",
          ipdNo: item?.ipdEntryId?.ipdNo || "",
          dischargeDate: item?.dischargeDate.split("T")[0] || "",
          dischargeTime: item?.dischargeTime || "",
          noOfDays: item?.noOfDays || "",
          patientName:
            (
              item?.ipdEntryId?.patientId?.patientNamePrefix + " "
              ) +
            (item?.ipdEntryId?.patientId?.patientName || ""),
          sex:  item?.ipdEntryId?.patientId?.sex || "",
          age:  item?.ipdEntryId?.patientId?.age || "",
          mobileNo:  item?.ipdEntryId?.patientId?.mobileNo || "",
          address:  item?.ipdEntryId?.patientId?.address || "",
          patientStatus: item.patientStatus || "",
          netAmount: item.netAmount || "",
          totalCharge: item.totalCharge || "",
          icdx: item.icdx || "",
          advance: item.advance || "",
          discountPercentage: item.discountPercentage || "",
          discountAmount: item.discountAmount || "",
          discountBy: item.discountBy || "",
          paymentMode: item.paymentMode || "",
          toPay: item.toPay || "",
          netAmount: item.netAmount || "",
          finalDiagnosis: item.finalDiagnosis || "",
          remark: item.remark || "",
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  const handleEditClick = id => {
    navigate(`/ipd/transaction/patient-discharge-entry/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePatientDischarge(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "8%" },
    { name: "IPD No", selector: row => row.ipdNo, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "20%" },
    { name: "Sex", selector: row => row.sex, sortable: true, width: "8%" },
    { name: "Age", selector: row => row.age, sortable: true, width: "8%" },
    { name: "Mobile No", selector: row => row.mobileNo, sortable: true, width: "12%" },
    { name: "Address", selector: row => row.address, sortable: true, width: "25%" },
    { name: "Discharge Date", selector: row => row.dischargeDate, sortable: true, width: "15%" },
    { name: "Discharge Time", selector: row => row.dischargeTime, sortable: true, width: "15%" },
    { name: "No of Days", selector: row => row.noOfDays, sortable: true, width: "10%" },
    { name: "Patient Status", selector: row => row.patientStatus, sortable: true, width: "15%" },
    { name: "Net Amount", selector: row => row.netAmount, sortable: true, width: "10%" },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Mobile No", key: "mobileNo" },
    { label: "Address", key: "address" },
    { label: "Discharge Date", key: "dischargeDate" },
    { label: "Time", key: "time" },
    { label: "No of Days", key: "noOfDays" },
    { label: "Patient Status", key: "patientStatus" },
    { label: "Net Amount", key: "netAmount" }
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="patient-discharge-entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>;

  return (
    <Fragment>
      <Breadcrumb parent="IPD" title="List Patient Discharge Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List Patient Discharge Entries</h5>
                  <Link
                    to={`/ipd/transaction/patient-discharge-entry/add`}
                  >
                    <button className="btn btn-primary" id="create">
                      Create Patient Discharge Entry
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New Patient Discharge Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by IPD No, Patient Name..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPatientDischargeEntry;
